function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(callback, limit) {
  var waiting = false; // Initially, we're not waiting
  return function () {
    // We return a throttled function
    if (!waiting) {
      // If we're not waiting
      callback.apply(this, arguments); // Execute users function
      waiting = true; // Prevent future invocations
      setTimeout(function () {
        // After a period of time
        waiting = false; // And allow future invocations
      }, limit);
    }
  };
}

/**
 * Wrapper around Object.assign that deletes null or undefined
 * keys from the provided object, making them fall back to
 * values in the defaults object.
 *
 *
 * @param {Object} defaults - An object with defaults for settings/config
 * @param {Object} provided - Provided settings/config object
 * @returns {Object} - Merged object
 */

function objectWithDefaults(defaults, provided) {
  filterObjectByValues(provided, (value) => {
    return value === null || value === undefined;
  });

  return Object.assign(defaults, provided);
}

function wrapAll(nodes, wrapper) {
  // Cache the current parent and previous sibling of the first node.
  var parent = nodes[0].parentNode;
  var previousSibling = nodes[0].previousSibling;

  // Place each node in wrapper.
  //  - If nodes is an array, we must increment the index we grab from
  //    after each loop.
  //  - If nodes is a NodeList, each node is automatically removed from
  //    the NodeList when it is removed from its parent with appendChild.
  for (var i = 0; nodes.length - i; wrapper.firstChild === nodes[0] && i++) {
    wrapper.appendChild(nodes[i]);
  }

  // Place the wrapper just after the cached previousSibling
  parent.insertBefore(wrapper, previousSibling.nextSibling);

  return wrapper;
}

function unwrap(wrapper) {
  // place childNodes in document fragment
  var docFrag = document.createDocumentFragment();
  while (wrapper.firstChild) {
    var child = wrapper.removeChild(wrapper.firstChild);
    docFrag.appendChild(child);
  }

  // replace wrapper with document fragment
  wrapper.parentNode.replaceChild(docFrag, wrapper);
}

function initTeleport(el) {
  if (!el) return;

  const teleportCandidates = el.querySelectorAll('[data-should-teleport]');

  if (teleportCandidates.length) {
    teleportCandidates.forEach((teleportCandidate) => {
      teleportCandidate.setAttribute(
        'x-teleport',
        teleportCandidate.dataset.shouldTeleport
      );
    });
  }
}

async function fetchSectionHTML(url, selector) {
  const res = await fetch(url);
  const fetchedSection = await res.text();
  const result = querySelectorInHTMLString(selector, fetchedSection);
  if (result === null) {
    return null;
  } else {
    return result.innerHTML;
  }
}

function fetchConfigDefaults(type = 'json') {
  return {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
      'Content-Type': 'application/json;',
      Accept: `application/${type}`,
    },
  };
}

function getPreviewThemeId() {
  try {
    return new URLSearchParams(window.location.search).get('preview_theme_id');
  } catch (e) {
    return null;
  }
}

function cartSectionsUrl() {
  return window.location.pathname + window.location.search;
}

function withPreviewTheme(url) {
  const preview = getPreviewThemeId();
  if (!preview || !url) return url;
  if (String(url).includes('preview_theme_id=')) return url;
  return String(url) + (String(url).includes('?') ? '&' : '?') + 'preview_theme_id=' + encodeURIComponent(preview);
}

function isSidecarCartHtml(html) {
  return typeof html === 'string' && (html.includes('custom-cart-items') || html.includes('custom-cart-footer'));
}

async function waitForSidecarCartIdle() {
  const started = Date.now();
  while (window.__sidecarCartFlushing && Date.now() - started < 4000) {
    await new Promise((resolve) => setTimeout(resolve, 30));
  }
}

async function fetchSidecarCartSections(options) {
  const force = options && options.force;
  const epoch = window.__sidecarCartEpoch || 0;
  const existing = window.__sidecarSectionsFetch;
  if (!force && existing && existing.promise && existing.epoch === epoch) {
    return existing.promise;
  }
  const promise = (async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', 'cart-items,cart-footer,cart-item-count,cart-live-region');
    const res = await fetch(url.pathname + url.search, { credentials: 'same-origin' });
    if (!res.ok) return null;
    return res.json();
  })();
  window.__sidecarSectionsFetch = { epoch, promise };
  return promise;
}

function sidecarBlockedGifts() {
  if (!window.__sidecarBlockedGifts) {
    window.__sidecarBlockedGifts = { handles: {}, variants: {}, keys: {} };
  }
  return window.__sidecarBlockedGifts;
}

function isSidecarGiftBlocked(lineKey, handle, variantId) {
  const blocked = window.__sidecarBlockedGifts;
  if (!blocked) return false;
  if (lineKey && blocked.keys[lineKey]) return true;
  if (handle && blocked.handles[handle]) return true;
  if (variantId != null && variantId !== '' && blocked.variants[String(variantId)]) return true;
  return false;
}

function stripBlockedGiftsFromTree(root) {
  if (!root || !window.__sidecarBlockedGifts) return;
  root.querySelectorAll('li[key]').forEach((li) => {
    const key = li.getAttribute('key');
    const handle = li.getAttribute('data-product-handle');
    const variant = li.getAttribute('data-gift-variant') || String(key || '').split(':')[0];
    if (isSidecarGiftBlocked(key, handle, variant)) li.remove();
  });
}

function recordSidecarGiftDismissals(lineKey, lineItemEl) {
  const handle = lineItemEl && lineItemEl.getAttribute('data-product-handle');
  const variantId =
    (lineItemEl && lineItemEl.getAttribute('data-gift-variant')) ||
    String(lineKey || '').split(':')[0];
  const stampedCampaign = lineItemEl && lineItemEl.getAttribute('data-gift-campaign');
  const blocked = sidecarBlockedGifts();
  if (lineKey) blocked.keys[lineKey] = true;
  if (handle) blocked.handles[handle] = true;
  if (variantId) blocked.variants[String(variantId)] = true;

  let store = {};
  try {
    store = JSON.parse(window.localStorage.getItem('giftbox_gift_dismissals_v1') || '{}');
  } catch (e) {
    store = {};
  }
  if (!store || typeof store !== 'object') store = {};

  const campaigns = {};
  if (stampedCampaign) campaigns[stampedCampaign] = true;
  try {
    const snap = JSON.parse(window.sessionStorage.getItem('giftbox_gift_lines_v1') || '[]');
    (snap || []).forEach((entry) => {
      if (!entry || !entry.key) return;
      const sep = String(entry.key).indexOf('::');
      if (sep > 0) campaigns[String(entry.key).slice(0, sep)] = true;
    });
    Object.keys(store).forEach((key) => {
      const sep = key.indexOf('::');
      if (sep > 0) campaigns[key.slice(0, sep)] = true;
    });
  } catch (e) {}

  const now = Date.now();
  const writeKey = (key) => {
    if (!key) return;
    store[key] = Object.assign({}, store[key] || {}, { dismissedAt: now });
  };
  Object.keys(campaigns).forEach((campaign) => {
    if (handle) writeKey(campaign + '::' + handle);
    if (variantId) writeKey(campaign + '::' + variantId);
  });
  try {
    const snap = JSON.parse(window.sessionStorage.getItem('giftbox_gift_lines_v1') || '[]');
    (snap || []).forEach((entry) => {
      if (!entry || !entry.key) return;
      const sameVariant = variantId && String(entry.variantId) === String(variantId);
      const sameHandle = handle && entry.handle && String(entry.handle) === handle;
      if (sameVariant || sameHandle) writeKey(entry.key);
    });
  } catch (e) {}

  try {
    window.localStorage.setItem('giftbox_gift_dismissals_v1', JSON.stringify(store));
  } catch (e) {}
  window.__sidecarLastGiftDismissAt = Date.now();
}

function markSidecarPaidRemoved(lineKey) {
  window.__sidecarLastPaidRemoveAt = Date.now();
  window.__sidecarPaidRemoveGeneration = (window.__sidecarPaidRemoveGeneration || 0) + 1;
  window.__sidecarRemovedPaidKeys = window.__sidecarRemovedPaidKeys || {};
  if (lineKey) window.__sidecarRemovedPaidKeys[lineKey] = Date.now();
}

function clearSidecarPaidRemoved() {
  window.__sidecarLastPaidRemoveAt = 0;
  window.__sidecarRemovedPaidKeys = {};
}

function clearSidecarBlockedGifts() {
  window.__sidecarBlockedGifts = { handles: {}, variants: {}, keys: {} };
}

function unstickBlockedGiftsPresentInCart(cart) {
  const blocked = window.__sidecarBlockedGifts;
  if (!blocked || !cart || !Array.isArray(cart.items)) return;
  cart.items.forEach((item) => {
    if (!item) return;
    if (item.key) delete blocked.keys[item.key];
    if (item.handle) delete blocked.handles[item.handle];
    const variantId = item.variant_id != null ? item.variant_id : item.id;
    if (variantId != null) delete blocked.variants[String(variantId)];
  });
}

async function reconcileSidecarCartWithAjax(options) {
  const attempts = (options && options.attempts) || 8;
  const delayMs = (options && options.delayMs) || 160;
  let lastCart = null;
  for (let i = 0; i < attempts; i++) {
    if (window.__sidecarCartFlushing) return lastCart;
    if (i) await new Promise((resolve) => setTimeout(resolve, delayMs));
    try {
      lastCart = await (await fetch(withPreviewTheme('/cart.js'), { credentials: 'same-origin' })).json();
    } catch (e) {
      continue;
    }
    if (!lastCart || !Array.isArray(lastCart.items)) continue;

    const root = document.querySelector('[data-cart-items]');
    const visibleKeys = [];
    if (root) {
      root.querySelectorAll('.custom-cart-item:not([hidden])').forEach((li) => {
        const key = li.getAttribute('key');
        if (key) visibleKeys.push(key);
      });
    }
    const cartKeys = lastCart.items.map((item) => item && item.key).filter(Boolean);
    const missing = cartKeys.some((key) => visibleKeys.indexOf(key) === -1);
    const extra = visibleKeys.some((key) => cartKeys.indexOf(key) === -1);
    if (window.Alpine && Alpine.store('cart_count')) {
      Alpine.store('cart_count').count = Number(lastCart.item_count) || cartKeys.length;
      if (Alpine.store('cart_count').setPending) Alpine.store('cart_count').setPending(false);
    }
    if (!missing && !extra) return lastCart;

    unstickBlockedGiftsPresentInCart(lastCart);
    const fresh = await fetchSidecarCartSections({ force: true });
    if (!fresh || !fresh['cart-items']) continue;
    document.body.dispatchEvent(
      new CustomEvent('shapes:modalcart:cartqtychange', {
        bubbles: true,
        detail: {
          response: Object.assign({}, lastCart, { sections: fresh }),
          preserveAutoGifts: true,
          epoch: window.__sidecarCartEpoch || 0,
        },
      })
    );
  }
  return lastCart;
}

function stripRemovedPaidFromTree(root) {
  const keys = window.__sidecarRemovedPaidKeys;
  if (!root || !keys) return;
  const now = Date.now();
  root.querySelectorAll('li[key]').forEach((li) => {
    const key = li.getAttribute('key');
    if (!key || !keys[key]) return;
    if (now - keys[key] > 8000) {
      delete keys[key];
      return;
    }
    li.remove();
  });
}

function cartJsonHasPaidItem(cart) {
  return !!(cart && Array.isArray(cart.items) && cart.items.some((item) => {
    if (!item) return false;
    const price = item.final_line_price != null ? item.final_line_price : item.line_price;
    const props = item.properties || {};
    return Number(price) > 0 && !props._Gifted && !props._giftbox_campaign_id;
  }));
}

async function sweepSidecarGiftOnlyCart() {
  try {
    const cart = await (await fetch(withPreviewTheme('/cart.js'), { credentials: 'same-origin' })).json();
    if (!cart || !Array.isArray(cart.items) || cartJsonHasPaidItem(cart)) return cart;
    const updates = {};
    cart.items.forEach((item) => {
      if (item && item.key) updates[item.key] = 0;
    });
    if (!Object.keys(updates).length) return cart;
    const config = fetchConfigDefaults();
    config.body = JSON.stringify({
      updates,
      sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
      sections_url: cartSectionsUrl(),
    });
    await fetch(withPreviewTheme((window.theme && theme.routes && theme.routes.cart_update_url) || '/cart/update.js'), config);
    return { items: [], item_count: 0 };
  } catch (e) {
    return null;
  }
}

function clearSidecarCascadeGiftState() {
  // Do not wipe shopper gift dismissals. Removing the trigger after
  // deleting gifts used to clear those flags and AddMate put them back.
}

async function enforceSidecarBlockedGifts() {
  const blocked = window.__sidecarBlockedGifts;
  if (!blocked) return;
  if (
    !Object.keys(blocked.keys).length &&
    !Object.keys(blocked.handles).length &&
    !Object.keys(blocked.variants).length
  ) {
    return;
  }
  try {
    const cart = await (await fetch(withPreviewTheme('/cart.js'), { credentials: 'same-origin' })).json();
    if (!cart || !Array.isArray(cart.items)) return;
    const paidLeft = cart.items.some((item) => {
      const price = item.final_line_price != null ? item.final_line_price : item.line_price;
      const props = item.properties || {};
      return Number(price) > 0 && !props._Gifted && !props._giftbox_campaign_id;
    });
    if (!paidLeft) {
      clearSidecarCascadeGiftState();
      return;
    }
    const updates = {};
    cart.items.forEach((item) => {
      if (
        isSidecarGiftBlocked(
          item.key,
          item.handle,
          item.variant_id != null ? item.variant_id : item.id
        )
      ) {
        updates[item.key] = 0;
      }
    });
    if (!Object.keys(updates).length) return;
    const config = fetchConfigDefaults();
    config.body = JSON.stringify({
      updates,
      sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
      sections_url: cartSectionsUrl(),
    });
    await fetch(withPreviewTheme((window.theme && theme.routes && theme.routes.cart_update_url) || '/cart/update.js'), config);
  } catch (e) {}
}

function nextSidecarCartEpoch() {
  window.__sidecarCartEpoch = (window.__sidecarCartEpoch || 0) + 1;
  window.__sidecarSectionsFetch = null;
  return window.__sidecarCartEpoch;
}

function isCurrentSidecarCartEpoch(epoch) {
  return epoch === (window.__sidecarCartEpoch || 0);
}

async function ensureSidecarCartSections(data) {
  if (!data) return data;
  if (data.__sidecarEnsured) return data;
  if (data.__sidecarEnsurePromise) return data.__sidecarEnsurePromise;
  data.__sidecarEnsurePromise = (async () => {
    const sections = data.sections || {};
    const itemsOk = isSidecarCartHtml(sections['cart-items']);
    const footerOk = !sections['cart-footer'] || isSidecarCartHtml(sections['cart-footer']);
    if (!itemsOk || !footerOk) {
      const fresh = await fetchSidecarCartSections();
      if (fresh) data.sections = Object.assign({}, sections, fresh);
    }
    data.__sidecarEnsured = true;
    return data;
  })();
  return data.__sidecarEnsurePromise;
}

function parseDOMFromString(htmlString) {
  window.___shapesDOMParser = window.___shapesDOMParser || new DOMParser();

  return window.___shapesDOMParser.parseFromString(htmlString, 'text/html');
}

function querySelectorInHTMLString(selector, htmlString) {
  return parseDOMFromString(htmlString).querySelector(selector);
}

let touchDevice = false;

window.setTouch = function () {
  touchDevice = true;
};

window.isTouch = function () {
  return touchDevice;
};

function getModalLabel(modalSlotName, slotEl) {
  if (Alpine.store('modals')[modalSlotName].open) {
    const labelSourceEl = Array.from(slotEl.children).filter((el) =>
      el.hasAttribute('data-modal-label')
    )[0];

    if (labelSourceEl) {
      return labelSourceEl.dataset.modalLabel;
    }
  }

  return false;
}
